import { LRUCache } from 'lru-cache';
import hash from 'object-hash';

/**
 * Today, 50 seems to be more than enough, and memory usage looks completely negligible.
 */
const MAX_UTILS_INSTANCES_COUNT = 50;

const utilsCache = new LRUCache<string, any>({ max: MAX_UTILS_INSTANCES_COUNT });

type GetIntlInstance = {
  (constructor: typeof Intl.NumberFormat, ...params: Parameters<typeof Intl.NumberFormat>): Intl.NumberFormat;
  (constructor: typeof Intl.PluralRules, ...params: Parameters<typeof Intl.PluralRules>): Intl.PluralRules;
};

/**
 * We noticed that, when Intl is used via polyfills (such as in React Native apps), instantiating Intl
 * classes dynamically causes a high resources consumption, leading to poor code execution performance
 * (for example, calling `new Intl.NumberFormat(...)` directly inside a utility function).
 * To prevent that, we use a LRU cache to store instances of Intl classes. Each instance is referenced
 * by a hash that represents the parameters of that class instance. This means, if two class instances
 * are created with the same parameters this function will return the existing class instance from the
 * cache instead of creating a new one.
 * One drawback of this solution is it can easily create memory leaks if we continuously add new class
 * instances to the cache unintentionally, for example if the hash computation is unstable. To prevent
 * that, we set a max cache size to 50 elements.
 */
export const getIntlInstance: GetIntlInstance = (constructor, ...params) => {
  // @ts-ignore
  const createInstance = () => new constructor(...params);

  if (!Intl.isPolyfilled) {
    return createInstance();
  }

  const paramsHash = hash([constructor.name, ...params]);

  if (utilsCache.has(paramsHash) === false) {
    utilsCache.set(paramsHash, createInstance());
  }

  return utilsCache.get(paramsHash)!;
};
